import {
  Content as DropdownContent,
  Label,
  Portal,
  Root,
  Separator,
  Sub,
  SubContent,
  SubTrigger,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import { Link } from '@tanstack/react-router';
import React, { type ReactNode, createContext, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ErrorField, type ErrorFieldProps } from './inputs/error-field';

export function DropdownLabel(props: { children: ReactNode }) {
  return (
    <Label className="font-body-2-m cursor-default select-none px-3 py-2 text-dark-gray">
      {props.children}
    </Label>
  );
}

export function DropdownSeparator() {
  return <Separator className="w-full border-b border-b-branding-gray" />;
}

export function AppDropdownBody(props: { children: ReactNode; className?: string }) {
  return (
    <div className={twMerge('grid min-w-[132px] grid-flow-row gap-1', props.className ?? '')}>
      {props.children}
    </div>
  );
}

export type DropdownItemHoverVariant = 'default' | 'accent';

export type DropdownItemProps = {
  label: string | React.ReactNode;
  onClick?: () => void;
  to?: string;
  params?: Record<string, string>;
  className?: string;
  disabled?: boolean;
  hoverVariant?: DropdownItemHoverVariant;
};

const DropdownContext = createContext<{
  closeOnSelect: boolean;
  close: () => void;
}>({ closeOnSelect: false, close: () => {} });

const hoverVariantClass: Record<DropdownItemHoverVariant, string> = {
  default: 'hover:bg-gray-100',
  accent: 'hover:bg-lavander-100',
};

export function DropdownMenuItem(props: DropdownItemProps) {
  const {
    label,
    onClick,
    to,
    params,
    className,
    disabled = false,
    hoverVariant = 'default',
  } = props;
  const { closeOnSelect, close } = React.useContext(DropdownContext);

  const itemClass = twMerge([
    `w-full z-50 flex text-left items-center justify-start p-2 font-body-2-m ${hoverVariantClass[hoverVariant]} cursor-pointer rounded-lg w-full focus:outline-none data-[highlighted]:bg-none`,
    'disabled:text-branding-gray disabled:cursor-default disabled:hover:bg-inherit',
    className,
  ]);

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (onClick != null) {
      onClick();
    }
    if (closeOnSelect) {
      close();
    }
  }

  if (to != null) {
    return (
      <Link
        to={to}
        params={params ?? {}}
        onClick={handleClick}
        className={itemClass}
        disabled={disabled}
      >
        {label}
      </Link>
    );
  }

  return (
    <button onClick={handleClick} className={itemClass} disabled={disabled}>
      {label}
    </button>
  );
}

export type DropdownMenuProps = {
  trigger: string | React.ReactNode;
  children: React.ReactNode;
  triggerClassName?: string;
  contentClassName?: string;
  position?: 'start' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  itemClassName?: string;
  sideOffset?: number;
  disabled?: boolean;
  contentFit?: boolean; // content dropdown fit to the trigger width
  closeOnSelect?: boolean;
  error?: ErrorFieldProps['error'];
  hideErrorText?: boolean;
  open?: boolean;
  onOpenChange?: (value: boolean) => unknown;
  container?: Element | null;
};

export function DropdownMenu(props: DropdownMenuProps) {
  const {
    trigger,
    sideOffset = 2,
    children,
    contentClassName = '',
    position = 'end',
    disabled = false,
    contentFit = false,
    closeOnSelect = false,
    error,
    triggerClassName = '',
    container = undefined,
    side = 'bottom',
  } = props;

  const [open, setOpen] = useState(false);

  const contentClass = twMerge([
    'relative z-50 p-2 min-w-[192px] rounded-lg border border-branding-gray bg-white shadow-2 mt-0.5',
    contentFit ? 'w-[var(--radix-dropdown-menu-trigger-width)]' : '',
    contentClassName,
  ]);

  const triggerClass = twMerge([
    'rounded-md data-[disabled]:bg-gray-100 data-[disabled]:hover:cursor-not-allowed data-[state=open]:border-branding-accent data-[state=open]:ring-2 data-[state=open]:ring-branding-accent-200',
    triggerClassName,
  ]);

  return (
    <Root open={props.open ?? open} onOpenChange={props.onOpenChange ?? setOpen}>
      <Trigger disabled={disabled} className={triggerClass}>
        {trigger}
      </Trigger>
      {!props.hideErrorText ? <ErrorField error={error} /> : null}
      <Portal container={container}>
        <DropdownContext.Provider value={{ closeOnSelect, close: () => setOpen(false) }}>
          <DropdownContent
            className={contentClass}
            align={position}
            sideOffset={sideOffset}
            side={side}
          >
            {children}
          </DropdownContent>
        </DropdownContext.Provider>
      </Portal>
    </Root>
  );
}

type DropdownSubTriggerProps = {
  children: React.ReactNode;
  className?: string;
  hoverVariant?: DropdownItemHoverVariant;
};

type DropdownSubContentProps = {
  children: React.ReactNode;
  className?: string;
  sideOffset?: number;
  alignOffset?: number;
};

export function DropdownSub(props: { children: React.ReactNode }) {
  const { children } = props;
  return <Sub>{children}</Sub>;
}

export function DropdownSubTrigger(props: DropdownSubTriggerProps) {
  const { children, className, hoverVariant = 'default' } = props;
  const subTriggerClass = twMerge([
    `w-full flex items-center justify-between ${hoverVariantClass[hoverVariant]} cursor-pointer rounded-lg focus:outline-none data-[highlighted]:bg-none`,
    className,
  ]);

  return <SubTrigger className={subTriggerClass}>{children}</SubTrigger>;
}

export function DropdownSubContent(props: DropdownSubContentProps) {
  const { children, className, sideOffset = 2, alignOffset = 0 } = props;
  const subContentClass = twMerge([
    'relative z-50 p-3 min-w-[192px] rounded-lg border border-v1-neutral-1 bg-white shadow-md',
    className,
  ]);

  return (
    <Portal>
      <SubContent className={subContentClass} sideOffset={sideOffset} alignOffset={alignOffset}>
        {children}
      </SubContent>
    </Portal>
  );
}
